import { useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { type ChosenImage } from './PlaceImageEditor';
import {
  HiddenInputs,
  PageHeader,
  PhoneDock,
  PlacingScreen,
  SelectionToolbar,
  WorkspaceSection,
  placementTarget,
  useNoticeTexts,
  type Workspace,
} from './PdfPageParts';
import { readImage } from './pdf-image';
import { outputName, saveBlob, usePdfWorkspace } from './use-pdf-workspace';

function useInputRefs() {
  const addInput = useRef<HTMLInputElement | null>(null);
  const replaceInput = useRef<HTMLInputElement | null>(null);
  const imageInput = useRef<HTMLInputElement | null>(null);
  return { addInput, replaceInput, imageInput };
}

type Placing = { key: string; image: ChosenImage };

/** The page's actions on the workspace: drop, download, extract and starting an image placement. */
function usePdfActions(
  ws: Workspace,
  single: Workspace['pages'][number] | undefined,
  setMessage: (message: string | null) => void,
  setPlacing: (placing: Placing) => void,
) {
  const { t } = useTranslation('tools');
  const onDrop = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    const list = [...event.dataTransfer.files].filter(
      (f) => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
    );
    if (list.length > 0) void ws.addFiles(list);
  };
  const downloadAll = async (): Promise<void> => {
    const blob = await ws.build(ws.pages);
    if (blob) saveBlob(blob, outputName(ws.files, 'modificato'));
  };
  const extract = async (): Promise<void> => {
    const blob = await ws.build(ws.pages.filter((p) => ws.selected.has(p.key)));
    if (blob) saveBlob(blob, outputName(ws.files, 'estratto'));
  };
  const startPlacing = async (file: File): Promise<void> => {
    if (!single) {
      setMessage(t('pdf.actions.imageOnePage'));
      return;
    }
    const image = await readImage(file);
    if (!image) {
      setMessage(t('pdf.place.imageError'));
      return;
    }
    setPlacing({ key: single.key, image });
  };
  const share = useShare(ws, setMessage);
  const select = (key: string, how: 'only' | 'toggle' | 'range'): void => {
    if (ws.busy) return;
    setMessage(null);
    ws.select(key, how);
  };
  return { onDrop, downloadAll, extract, startPlacing, share, select };
}

/**
 * «PDF» (#387, decision 98; boards approved in decision 99): one workspace instead of a tool per
 * task. Files added become one grid of pages; the selection can be extracted, replaced, rotated,
 * deleted, or given an image. Everything runs in the browser through the PDF engine.
 */
export function PdfPage() {
  const { t } = useTranslation('tools');
  const ws = usePdfWorkspace();
  const refs = useInputRefs();
  const { addInput, replaceInput, imageInput } = refs;
  const [placing, setPlacing] = useState<Placing | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const notices = useNoticeTexts(ws, message);

  const selectedCount = ws.selected.size;
  const single = selectedCount === 1 ? ws.pages.find((p) => ws.selected.has(p.key)) : undefined;
  const empty = ws.pages.length === 0;
  const pickAdd = (): void => addInput.current?.click();

  const { onDrop, downloadAll, extract, startPlacing, share, select } = usePdfActions(
    ws,
    single,
    setMessage,
    setPlacing,
  );

  if (placing && placementTarget(ws, placing.key)) {
    const close = (): void => {
      URL.revokeObjectURL(placing.image.url);
      setPlacing(null);
    };
    return <PlacingScreen ws={ws} placing={placing} onClose={close} />;
  }

  const toolbar = (phone: boolean) => (
    <SelectionToolbar
      phone={phone}
      selectedCount={selectedCount}
      busy={ws.busy}
      onExtract={() => void extract()}
      onReplace={() => replaceInput.current?.click()}
      onRotate={ws.rotateSelected}
      onImage={() =>
        single ? imageInput.current?.click() : setMessage(t('pdf.actions.imageOnePage'))
      }
      onDelete={ws.deleteSelected}
    />
  );

  return (
    <div className="space-y-6" onDragOver={(e) => e.preventDefault()} onDrop={onDrop}>
      <PageHeader
        showActions={!empty}
        busy={ws.busy}
        onAdd={pickAdd}
        onDownload={() => void downloadAll()}
      />
      <HiddenInputs
        {...refs}
        onAdd={(files) => void ws.addFiles(files)}
        onReplace={(file) => void ws.replaceSelected(file)}
        onImage={(file) => void startPlacing(file)}
      />

      <WorkspaceSection
        ws={ws}
        notices={notices}
        desktopToolbar={
          selectedCount > 0 && <div className="hidden sm:block">{toolbar(false)}</div>
        }
        onAdd={pickAdd}
        onSelect={select}
      />

      {!empty && (
        <PhoneDock
          toolbar={selectedCount > 0 && toolbar(true)}
          busy={ws.busy}
          onShare={() => void share()}
          onDownload={() => void downloadAll()}
        />
      )}
    </div>
  );
}

/**
 * Sharing the built file. The share sheet needs the tap's user activation, which a long build can
 * outlast. The built file is kept for the same pages, so a second tap shares at once.
 */
function useShare(ws: Workspace, setMessage: (message: string | null) => void) {
  const { t } = useTranslation('tools');
  const cache = useRef<{ pages: Workspace['pages']; file: File } | null>(null);
  return async (): Promise<void> => {
    if (typeof navigator.canShare !== 'function') {
      setMessage(t('pdf.notices.shareUnavailable'));
      return;
    }
    let file = cache.current?.pages === ws.pages ? cache.current.file : null;
    if (!file) {
      const blob = await ws.build(ws.pages);
      if (!blob) return;
      file = new File([blob], outputName(ws.files, 'modificato'), { type: 'application/pdf' });
      cache.current = { pages: ws.pages, file };
    }
    if (!navigator.canShare({ files: [file] })) {
      setMessage(t('pdf.notices.shareUnavailable'));
      return;
    }
    try {
      await navigator.share({ files: [file] });
      setMessage(null);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'NotAllowedError') {
        setMessage(t('pdf.notices.shareAgain'));
      }
    }
  };
}
